import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Items } from 'src/app/core/models/common/items.model';
import { IMAGE_SRC, STATUS_OPTIONS } from '../../../shared-constants';

@Component({
  selector: 'app-table-header-dropdown',
  templateUrl: './table-header-dropdown.component.html',
  styleUrls: ['./table-header-dropdown.component.scss'],
  standalone: false,
})
export class TableHeaderDropdownComponent implements OnInit, OnChanges {
  private readonly elementRef = inject(ElementRef);

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.showDropdown &&
      !this.elementRef.nativeElement.contains(event.target)
    ) {
      this.showDropdown = false;
      this.dropdownChange.emit('');
    }
  }

  private static readonly CHECKBOX_SEARCH_WIDTH_CLASSES: Record<
    number,
    string
  > = {
    60: 'checkbox-search-width-60',
    72: 'checkbox-search-width-72',
    76: 'checkbox-search-width-76',
    100: 'checkbox-search-width-100',
    120: 'checkbox-search-width-120',
    130: 'checkbox-search-width-130',
    140: 'checkbox-search-width-140',
    150: 'checkbox-search-width-150',
    153: 'checkbox-search-width-153',
    180: 'checkbox-search-width-180',
    200: 'checkbox-search-width-200',
    207: 'checkbox-search-width-207',
    209: 'checkbox-search-width-209',
    220: 'checkbox-search-width-220',
    230: 'checkbox-search-width-230',
    240: 'checkbox-search-width-240',
    299: 'checkbox-search-width-299',
    339: 'checkbox-search-width-339',
  };

  @Input({ required: true }) columnText!: string;
  @Input({ required: true }) searchResults!: Items[];
  @Input({ required: true }) showDropdown!: boolean;
  @Input({ required: true }) resetData!: BehaviorSubject<boolean>;
  @Input() displayedTitle = STATUS_OPTIONS.ALL_SELECTED;
  @Input() textInputWidth = 100;
  @Input() rightAlign = false;

  @Output() checkboxesUpdated = new EventEmitter<string | string[]>();
  @Output() dropdownChange = new EventEmitter<string>();

  destroy = inject(DestroyRef);

  readonly IMAGES = IMAGE_SRC;
  readonly SELECT_ALL_LABEL = STATUS_OPTIONS.ALL_SELECTED;
  readonly STATUS_OPTIONS = STATUS_OPTIONS;

  selectAllModel = true;
  checkedData!: Items[];

  get checkboxSearchWidthClass(): string {
    return (
      TableHeaderDropdownComponent.CHECKBOX_SEARCH_WIDTH_CLASSES[
        this.textInputWidth
      ] ?? 'checkbox-search-width-default'
    );
  }

  ngOnInit(): void {
    this.resetData
      .asObservable()
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((value) => value),
      )
      .subscribe(() => this.resetValues());
  }

  ngOnChanges(): void {
    if (this.searchResults?.length > 0) {
      this.searchResults.sort((a, b) =>
        a?.id.toUpperCase().localeCompare(b?.id.toUpperCase()),
      );
    }
  }

  selectAll() {
    setTimeout(() => {
      this.selectAllForCheckedColumn();
      this.updateCheckbox();
    });
  }

  updateCheckbox() {
    setTimeout(() => {
      this.handleCheckBoxSelection();
      this.displayedTitle = this.handleDropdownText();
      this.checkedData = this.searchResults.filter((value) => value.selected);
      this.checkboxesUpdated.emit(this.transformData());
    });
  }

  updateDropdown() {
    this.dropdownChange.emit('');
  }

  get toolTipText() {
    const filteredData = this.searchResults?.filter(
      (option) => option.selected,
    );
    return filteredData?.map((option) => option.id).join(', ');
  }

  get showTooltip() {
    const filteredData = this.searchResults?.filter(
      (option) => option.selected,
    );
    return filteredData?.length > 1;
  }

  private transformData() {
    let transformedResults;
    switch (true) {
      case this.selectAllModel:
        transformedResults = '';
        break;
      case this.checkedData.length === 0:
        transformedResults = 'n/a';
        break;
      default:
        transformedResults = this.checkedData.map((value) => value.id);
        break;
    }
    return transformedResults;
  }

  private handleDropdownText() {
    const filteredData = this.searchResults.filter((option) => option.selected);
    switch (true) {
      case filteredData.length == 0:
        return '';
      case filteredData.length == 1:
        return filteredData.map((option) => option.id)[0];
      case filteredData.length === this.searchResults.length:
        return STATUS_OPTIONS.ALL_SELECTED;
      default:
        return STATUS_OPTIONS.SOME_SELECTED;
    }
  }

  private handleCheckBoxSelection(): void {
    let selected = 0;
    this.searchResults.forEach((el) => {
      if (el.selected) {
        selected++;
      }
    });

    this.selectAllModel = selected === this.searchResults.length;
  }

  private selectAllForCheckedColumn(): void {
    this.searchResults.forEach((item) => {
      item.selected = this.selectAllModel;
    });
  }

  private resetValues() {
    this.searchResults.forEach((value) => {
      value.selected = true;
    });
    this.selectAllModel = true;
    this.displayedTitle = this.STATUS_OPTIONS.ALL_SELECTED;
  }
}
